import React, { useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FormControl, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Palette, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface BrandColorSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const DEFAULT_COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#000000", // Black
];

const BrandColorSelector = ({ value, onChange, disabled = false }: BrandColorSelectorProps) => {
  const isCustomColor = !DEFAULT_COLORS.includes(value);
  const [customColor, setCustomColor] = useState(isCustomColor ? value : "#3B82F6");

  // Handle color change from presets or custom input
  const handleColorChange = (selectedValue: string) => {
    onChange(selectedValue);
  };

  // Handle custom color input change
  const handleCustomColorChange = (newColor: string) => {
    setCustomColor(newColor);
    onChange(newColor);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {DEFAULT_COLORS.map((color) => (
          <div key={color} className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={() => handleColorChange(color)}
              className="h-10 w-10 rounded-md border-2 cursor-pointer focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all"
              style={{ 
                backgroundColor: color,
                borderColor: value === color ? "hsl(var(--primary))" : "transparent",
                transform: value === color ? "scale(1.1)" : "scale(1)"
              }}
              disabled={disabled}
              aria-label={`Select color ${color}`}
            />
          </div>
        ))}

        {/* Custom Color Picker */}
        <div className="flex flex-col items-center gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="h-10 w-10 rounded-md border-2 cursor-pointer focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all flex items-center justify-center"
                style={{ 
                  backgroundColor: isCustomColor ? value : "#f3f4f6",
                  borderColor: isCustomColor ? "hsl(var(--primary))" : "#d1d5db",
                  transform: isCustomColor ? "scale(1.1)" : "scale(1)"
                }}
                disabled={disabled}
                aria-label="Select custom color"
              >
                {!isCustomColor && (
                  <Plus className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Custom Color</span>
                </div>
                
                <div className="grid grid-cols-7 gap-2">
                  {[
                    "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", 
                    "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9",
                    "#F8C471", "#82E0AA", "#F1948A", "#D2B4DE",
                    "#AED6F1", "#A3E4D7", "#F9E79F", "#FADBD8", "#D5DBDB",
                    "#2C3E50", "#34495E", "#7F8C8D", "#95A5A6", "#BDC3C7",
                    "#ECF0F1", "#FFFFFF"
                  ].map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="h-8 w-8 rounded border-2 cursor-pointer hover:scale-110 transition-transform"
                      style={{ 
                        backgroundColor: color,
                        borderColor: value === color ? "hsl(var(--primary))" : "transparent"
                      }}
                      onClick={() => handleCustomColorChange(color)}
                    />
                  ))}
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => handleCustomColorChange(e.target.value)}
                      className="h-10 w-16 rounded cursor-pointer border border-gray-200"
                      style={{ WebkitAppearance: 'none', appearance: 'none' }}
                    />
                    <Input
                      type="text"
                      value={value}
                      onChange={(e) => handleCustomColorChange(e.target.value)}
                      placeholder="#3B82F6"
                      className="font-mono flex-1"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    Use the color picker or enter a hex code
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <span className="text-xs text-gray-500 text-center">Custom</span>
        </div>
      </div>

      {/* Hidden RadioGroup to maintain form control behavior */}
      <RadioGroup
        value={isCustomColor ? "custom" : value}
        onValueChange={handleColorChange}
        className="hidden"
        disabled={disabled}
      >
        {DEFAULT_COLORS.map((color) => (
          <FormItem key={color} className="hidden">
            <FormControl>
              <RadioGroupItem value={color} id={color} />
            </FormControl>
          </FormItem>
        ))}
        <FormItem className="hidden">
          <FormControl>
            <RadioGroupItem value="custom" id="custom-color" />
          </FormControl>
        </FormItem>
      </RadioGroup>
    </div>
  );
};

export default BrandColorSelector;
